import type { ProcessedImage, ProcessingOptions, WorkerRequest, WorkerResponse } from "../types";
import { savingsPercent } from "./format";
import { processInBrowserCanvas } from "./browserProcessor";

type QueueItem = {
  originalSize: number;
  options: ProcessingOptions;
  onProgress: (progress: number) => void;
  resolve: (image: ProcessedImage) => void;
  reject: (error: Error) => void;
};

export class ImageProcessingClient {
  private worker: Worker | null = supportsWorkerCanvas()
    ? new Worker(new URL("../workers/imageWorker.ts", import.meta.url), {
        type: "module"
      })
    : null;

  private pending = new Map<string, QueueItem>();

  constructor() {
    if (!this.worker) return;

    this.worker.addEventListener("message", (event: MessageEvent<WorkerResponse>) => {
      const message = event.data;
      const item = this.pending.get(message.id);
      if (!item) return;

      if (message.type === "progress") {
        item.onProgress(message.progress);
        return;
      }

      this.pending.delete(message.id);

      if (message.type === "error") {
        item.reject(new Error(message.error));
        return;
      }

      item.resolve({
        blob: message.blob,
        url: URL.createObjectURL(message.blob),
        filename: message.filename,
        size: message.size,
        width: message.width,
        height: message.height,
        format: message.format,
        savingsPercent: savingsPercent(item.originalSize, message.size),
        wasLargerThanOriginal: message.size > item.originalSize,
        optimizationApplied: message.optimizationApplied
      });
    });

    this.worker.addEventListener("error", () => {
      this.rejectAll("The image worker stopped unexpectedly. Try a smaller image or reload the page.");
    });

    this.worker.addEventListener("messageerror", () => {
      this.rejectAll("The browser could not transfer image data to the worker.");
    });
  }

  process(file: File, sourceName: string, originalSize: number, options: ProcessingOptions, onProgress: (progress: number) => void) {
    if (!this.worker) {
      return processInBrowserCanvas(file, sourceName, originalSize, options, onProgress);
    }

    const worker = this.worker;
    const id = crypto.randomUUID();
    const payload: WorkerRequest = { id, file, sourceName, originalSize, options };

    return new Promise<ProcessedImage>((resolve, reject) => {
      this.pending.set(id, { originalSize, options, onProgress, resolve, reject });
      worker.postMessage(payload);
    });
  }

  dispose() {
    this.worker?.terminate();
    this.pending.clear();
  }

  private rejectAll(message: string) {
    this.pending.forEach((item) => item.reject(new Error(message)));
    this.pending.clear();
  }
}

function supportsWorkerCanvas() {
  return (
    typeof Worker !== "undefined" &&
    typeof OffscreenCanvas !== "undefined" &&
    typeof createImageBitmap !== "undefined" &&
    "convertToBlob" in OffscreenCanvas.prototype
  );
}
